import type { Dispatch, SetStateAction } from "react"
import { createContext, useContext, useRef, useState } from "react"

import { Box, Stack, Typography } from "@mui/material"
import { alpha, lighten } from "@mui/material/styles"
import { noop } from "lodash"

import type { FC, FCWC } from ".."
import { Button, ContainerGrid, IconButton, Image, SvgIcon } from ".."
import { type AdBanner } from "./Types"

let nextId = 0
const GlobalAdBannersContext = createContext<{
  active: [index: number, setIndex: Dispatch<SetStateAction<number>>]
  numSlides: number
}>({
  active: [0, noop],
  numSlides: 0,
})

type GlobalAddBannerProps = {
  items?: AdBanner[]
}

const GlobalAdBanners: FC<GlobalAddBannerProps> = ({ items = [] }) => {
  const [index, setIndex] = useState(0)
  nextId = 0

  return (
    <GlobalAdBannersContext.Provider value={{ active: [index, setIndex], numSlides: items.length }}>
      <ContainerGrid position="relative">
        <Stack position="relative" height="100%" alignItems="center" justifyContent="center">
          <IconButton
            aria-label="Previous"
            variant="secondary"
            icon={<SvgIcon variant="arrow" size={20} />}
            sx={{
              display: { xs: "none", lg: items.length > 1 ? "inline-flex" : "none" },
            }}
            onClick={() => setIndex((index) => (index - 1 < 0 ? items.length - 1 : index - 1))}
          />
        </Stack>
        <Box
          gridColumn={{ xs: "1 / -1", lg: "2 / -2" }}
          pb={10}
          overflow="hidden"
          position="relative"
        >
          <Box
            display="grid"
            gridAutoFlow="row"
            gridAutoRows="minmax(0, 1fr)"
            position="relative"
            sx={{ isolation: "isolate" }}
          >
            {items.map((item, i) => (
              <Banner key={item.title || item.imageUrl || `global-ad-banner-${i}`}>
                <BannerContent {...item} />
                <BannerImage src={item.imageUrl} alt={item.title} />
              </Banner>
            ))}
          </Box>
        </Box>

        <Stack position="relative" height="100%" alignItems="center" justifyContent="center">
          <IconButton
            aria-label="Next"
            variant="secondary"
            icon={<SvgIcon variant="arrow" size={20} sx={{ transform: "rotate(180deg)" }} />}
            sx={{
              display: { xs: "none", lg: items.length > 1 ? "inline-flex" : "none" },
            }}
            onClick={() => setIndex((index) => (index + 1 > items.length - 1 ? 0 : index + 1))}
          />
        </Stack>
      </ContainerGrid>
    </GlobalAdBannersContext.Provider>
  )
}
export default GlobalAdBanners

const GlobalAdSlideContext = createContext<{
  index: number
}>({
  index: 0,
})

const Banner: FCWC = ({ children }) => {
  const {
    active: [activeIndex, setActiveIndex],
    numSlides,
  } = useContext(GlobalAdBannersContext)
  const { current: slideIndex } = useRef(nextId++)
  const slideOffset = (((slideIndex - activeIndex) % numSlides) + numSlides) % numSlides
  const isActive = slideIndex === activeIndex

  // if (!isActive) return null

  return (
    <Box
      width="100%"
      bgcolor={(theme) => theme.palette["secondary.300"].main}
      color={(theme) => theme.palette["secondary.800"].main}
      borderRadius={1.5}
      zIndex={Math.abs(numSlides - slideOffset)}
      sx={{
        position: slideOffset === 0 ? "relative" : "absolute",
        bottom: 0,
        left: "50%",
        bgcolor: (theme) =>
          lighten(theme.palette["secondary.300"].main, Math.min(0.35 * slideOffset, 1)),
        transform: ` translate(-50%, ${20 * slideOffset - 1}px) scaleX(${1 - 0.05 * slideOffset})`,
        transformOrigin: "bottom center",
        transition: (theme) =>
          theme.transitions.create("all", {
            duration: theme.transitions.duration.standard * 2,
            easing: "cubic-bezier(0.18, 0.98, 0.45, 1)",
          }),
        // width: `calc(100% - ${40 * slideOffset}px)`,
        overflow: "hidden",
      }}
    >
      <GlobalAdSlideContext.Provider value={{ index: slideIndex }}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            lg: "auto clamp(0px, 39.08%, 494px)",
          }}
          alignItems="center"
          justifyContent="space-between"
          height={{ lg: 440 }}
          p={{ xs: 1, lg: 5 }}
          sx={{
            opacity: isActive ? 1 : 0,
            transition: (theme) =>
              theme.transitions.create("opacity", {
                duration: theme.transitions.duration.standard * 2,
                easing: "cubic-bezier(0.18, 0.98, 0.45, 1)",
              }),
          }}
        >
          {children}
        </Box>
      </GlobalAdSlideContext.Provider>
      {/* <Box position="absolute" bottom={0} left={0}>
        {slideIndex} {slideOffset} {numSlides - slideOffset}
      </Box> */}
    </Box>
  )
}

type BannerContentProps = {
  title: string
  description: string
  link: {
    label: string
    url: string
  }
}
const BannerContent: FC<BannerContentProps> = ({ title, description, link }) => {
  const {
    active: [activeIndex, setIndex],
    numSlides,
  } = useContext(GlobalAdBannersContext)
  const { index: slideIndex } = useContext(GlobalAdSlideContext)

  const isActive = slideIndex === activeIndex

  return (
    <Box
      order={{ xs: 2, lg: 1 }}
      maxWidth={{ lg: 711 }}
      py={5}
      px={{ xs: 4, md: 5, lg: 20 }}
      sx={{
        opacity: isActive ? 1 : 0,
        transition: (theme) =>
          theme.transitions.create("all", {
            duration: theme.transitions.duration.standard * 2,
          }),
      }}
    >
      <Box mb={5}>
        <Typography
          variant="heading.lg"
          component="h2"
          sx={{
            "overflow": "hidden",
            "display": "-webkit-box",
            "WebkitBoxOrient": "vertical",
            "WebkitLineClamp": 3,

            "& > *": {
              display: "inline",
            },
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box mb={10}>
        <Typography
          variant="body.md"
          component="div"
          dangerouslySetInnerHTML={{ __html: description }}
          sx={{
            "overflow": "hidden",
            "display": "-webkit-box",
            "WebkitBoxOrient": "vertical",
            "WebkitLineClamp": 4,

            "& > *": {
              display: "inline",
            },
          }}
        />
      </Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={4}>
        <Button variant="neutral.secondary" href={link.url}>
          {link.label}
        </Button>
        <Stack direction="row" spacing={4} sx={{ display: { xs: "flex", lg: "none" } }}>
          <IconButton
            aria-label="Previous"
            variant="secondary"
            icon={
              <SvgIcon
                variant="arrow"
                size={14}
                sx={{
                  fill: (theme) => theme.palette["secondary.800"].main,
                  transform: "rotate(-90deg)",
                }}
              />
            }
            sx={{
              width: 48,
              height: 48,
              borderColor: (theme) => alpha(theme.palette["secondary.800"].main, 0.18),
            }}
            onClick={() => setIndex((index) => (index + 1 > numSlides - 1 ? 0 : index + 1))}
          />
          <IconButton
            aria-label="Next"
            variant="secondary"
            icon={
              <SvgIcon
                variant="arrow"
                size={14}
                sx={{
                  fill: (theme) => theme.palette["secondary.800"].main,
                  transform: "rotate(90deg)",
                }}
              />
            }
            sx={{
              width: 48,
              height: 48,
              borderColor: (theme) => alpha(theme.palette["secondary.800"].main, 0.18),
            }}
            onClick={() => setIndex((index) => (index - 1 < 0 ? numSlides - 1 : index - 1))}
          />
        </Stack>
      </Stack>
    </Box>
  )
}

type BannerImageProps = { src: string; alt: string }
const BannerImage: FC<BannerImageProps> = ({ src, alt }) => {
  return (
    <Box order={{ xs: 1, lg: 2 }} width="100%" position="relative" alignSelf="stretch">
      <Image
        src={src}
        alt={alt}
        width="100%"
        height="100%"
        borderRadius={1.5}
        overflow="hidden"
        sx={{ aspectRatio: { md: "5/4" }, display: "block", objectFit: "cover" }}
      />
    </Box>
  )
}
